import { useAllVouchers, useViewVoucherReport } from "http-hooks/voucher/useVoucher";
import DeleteVoucher from "components/voucher/DeleteVoucher";
import { useState, useEffect } from "react";
import useTableSort, { withSort } from "hooks/useTableSort";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";
import useModalStore from "store/modals";
import { ActionButton } from "components/ui/Table";
import InputSearch from "components/ui/InputSearch";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import Modal from "components/ui/modals";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import MasterPage, {
  FilterRow,
  FilterCell,
  ClearFiltersButton,
  AddButton,
  EditAction,
  DeleteAction,
} from "components/ui/MasterPage";
import { EyeIcon } from "@heroicons/react/24/outline";
import { toIndianCurrencyFormat } from "utils/amount";

const typeOptions = [
  { value: "", label: "All Types" },
  { value: "MR", label: "Money Receipt" },
  { value: "JR", label: "Journal Voucher" },
  { value: "BC", label: "Bank Receipt" },
  { value: "BD", label: "Bank Payment" },
  { value: "CC", label: "Cash Receipt" },
  { value: "CD", label: "Cash Payment" },
];

const createVoucherOptions = [
  { value: "MR", label: "Money Receipt" },
  { value: "JR", label: "Journal Voucher" },
  { value: "CC", label: "Cash Receipt" },
  { value: "CD", label: "Cash Payment" },
  { value: "BC", label: "Bank Receipt" },
  { value: "BD", label: "Bank Payment" },
];

const voucherTypeLabels = {
  MR: "Money Receipt",
  JR: "Journal Voucher",
  BC: "Bank Receipt",
  BD: "Bank Payment",
  CC: "Cash Receipt",
  CD: "Cash Payment",
};

const initialFilter = {
  search: "",
  trtyp: "",
  account: "",
  fromDate: "",
  toDate: "",
  page: 1,
  limit: 10,
};

export default function VoucherMaster({ type: propType = "" }) {
  const [searchParams] = useSearchParams();
  const queryType = searchParams.get('type') || propType;

  const [viewingReportId, setViewingReportId] = useState(null);
  const [selectedType, setSelectedType] = useState(
    queryType ? typeOptions.find(opt => opt.value === queryType) : null
  );
  const [selectedCreateType, setSelectedCreateType] = useState(null);
  const [showVoucherTypePicker, setShowVoucherTypePicker] = useState(false);
  const [filter, setFilter] = useState({
    ...initialFilter,
    trtyp: queryType || "",
  });
  const tableSort = useTableSort();
  const { openModal } = useModalStore();
  const { mutateAsync: viewReport } = useViewVoucherReport();
  const navigate = useNavigate();
  const { data: accountsResponse } = useAllPartySupMasts({
    level: 0,
    grplevel: 0,
    ag_status: 1,
    noPagination: true,
  });
  const accountOptions = accountsResponse?.data || [];

  useEffect(() => {
    const newType = searchParams.get('type') || propType;
    setSelectedType(newType ? typeOptions.find(opt => opt.value === newType) : null);
    setFilter(prev => ({
      ...prev,
      trtyp: newType || "",
      page: 1,
    }));
  }, [searchParams, propType]);

  const openSetModal = (id, voucherType = queryType) => {
    if (!id && !queryType) {
      setShowVoucherTypePicker(true);
      return;
    }
    openModal('voucher', id, { type: voucherType });
  };

  const createSelectedVoucher = () => {
    if (!selectedCreateType) return;
    setShowVoucherTypePicker(false);
    openModal("voucher", null, { type: selectedCreateType.value });
  };

  const openPrintModal = (voucher) => {
    openModal('printVoucher', null, { voucher });
  };

  const handleViewReport = async (voucher) => {
    try {
      setViewingReportId(voucher.trid);
      const data = await viewReport(voucher.trid);

      if (data.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);

        const route = getDocumentViewerRoute(voucher.trid, {
          htmlUrl,
          pdfUrl,
          documentNumber: voucher.docno,
          documentType: voucher.trtyp === 'MR' ? 'Money Receipt' : 'Voucher'
        });

        navigate(route);
      }
    } catch (error) {
      console.error('Error viewing report:', error);
    } finally {
      setViewingReportId(null);
    }
  };

  const handlePrintClick = (voucher) => {
    if (voucher.trtyp === 'MR') {
      openPrintModal(voucher);
    } else {
      handleViewReport(voucher);
    }
  };

  const handleSearch = (searchTerm) => {
    setFilter((prev) => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handleTypeChange = (selectedOption) => {
    setSelectedType(selectedOption);
    setFilter((prev) => ({
      ...prev,
      trtyp: selectedOption?.value || "",
      page: 1,
    }));
  };

  const handleAccountChange = (account) => {
    setFilter((prev) => ({
      ...prev,
      account: account?.ps_Id || "",
      page: 1,
    }));
  };

  const handleDateChange = (event) => {
    const { name, value } = event.target;
    setFilter((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedType(queryType ? typeOptions.find(opt => opt.value === queryType) : null);
    setFilter({
      ...initialFilter,
      trtyp: queryType || "",
    });
  };

  const {
    isLoading: isAllVouchersLoading,
    data: { data: allVouchers, page: currentPage, totalPages } = [],
  } = useAllVouchers(withSort(filter, tableSort));

  const getPageTitle = () => {
    return voucherTypeLabels[queryType] || "Voucher Master";
  };

  const getAddButtonText = () => {
    return queryType
      ? `Add ${voucherTypeLabels[queryType] || "Voucher"}`
      : "Add Voucher";
  };

  const columns = [
    { key: "docno", header: "Voucher No.", sortable: true },
    {
      key: "trdate",
      header: "Date",
      sortable: true,
      render: (item) =>
        item.trdate &&
        new Date(item.trdate).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      key: "trtyp",
      header: "Type",
      sortable: true,
      render: (item) => (
        <span
          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
            item.trtyp === 'MR'
              ? 'bg-blue-50 text-blue-700 ring-blue-600/20'
              : item.trtyp === 'JR'
                ? 'bg-green-50 text-green-700 ring-green-600/20'
                : 'bg-gray-50 text-gray-700 ring-gray-600/20'
          }`}
        >
          {voucherTypeLabels[item.trtyp] || item.trtyp}
        </span>
      ),
    },
    {
      key: "accredit",
      header: "Account Credit",
      sortable: true,
      render: (item) => item.creditAccount?.name || item.accredit,
    },
    {
      key: "acdebit",
      header: "Account Debit",
      sortable: true,
      render: (item) => item.debitAccount?.name || item.acdebit,
    },
    {
      key: "tramount",
      header: "Amount",
      sortable: true,
      render: (item) => toIndianCurrencyFormat(item.tramount),
    },
    { key: "actions", header: "Actions", isActions: true },
  ];

  return (
    <MasterPage
      title={getPageTitle()}
      headerActions={
        <AddButton onClick={() => openSetModal()}>
          {getAddButtonText()}
        </AddButton>
      }
      filters={
        <>
          <FilterRow>
            <FilterCell>
              <InputSearch
                value={filter.search}
                onChange={(event) =>
                  setFilter((prev) => ({ ...prev, search: event.target.value }))
                }
                onSearch={handleSearch}
                label="Search Transactions"
                placeholder="Voucher no., narration or transaction no."
              />
            </FilterCell>
            {!queryType && (
              <FilterCell>
                <Select
                  options={typeOptions}
                  value={selectedType}
                  onChange={handleTypeChange}
                  label="Type"
                  placeholder="Select type"
                  isClearable={true}
                  isSearchable={false}
                />
              </FilterCell>
            )}
            <FilterCell>
              <Input
                label="From Date"
                type="date"
                name="fromDate"
                value={filter.fromDate}
                onChange={handleDateChange}
                withinFY
              />
            </FilterCell>
            <FilterCell>
              <Input
                label="To Date"
                type="date"
                name="toDate"
                value={filter.toDate}
                onChange={handleDateChange}
                withinFY
              />
            </FilterCell>
          </FilterRow>
          <FilterRow>
            <FilterCell>
              <Select
                label="Account"
                options={accountOptions}
                value={
                  accountOptions.find((account) => account.ps_Id === filter.account) ||
                  null
                }
                onChange={handleAccountChange}
                getOptionValue={(account) => account.ps_Id}
                getOptionLabel={(account) => account.name}
                placeholder="All accounts"
                isClearable
                isSearchable
              />
            </FilterCell>
            <ClearFiltersButton onClick={clearFilters} />
          </FilterRow>
        </>
      }
      columns={columns}
      sort={tableSort}
      data={allVouchers}
      isLoading={isAllVouchersLoading}
      rowKey="trid"
      deleteComponent={DeleteVoucher}
      renderActions={(item, setDeleteItemId) => (
        <>
          <ActionButton
            variant="primary"
            onClick={() => handlePrintClick(item)}
            title="Print Voucher"
            disabled={viewingReportId === item.trid}
          >
            <EyeIcon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">Print voucher, {item.docno}</span>
          </ActionButton>
          <EditAction
            onClick={() => openSetModal(item.trid, item.trtyp)}
            title="Edit Voucher"
          />
          <DeleteAction
            onClick={() => setDeleteItemId(item.trid)}
            title="Delete Voucher"
          />
        </>
      )}
      pagination={{
        currentPage,
        totalPages,
        onPageChange: (page) => setFilter((prev) => ({ ...prev, page })),
      }}
      extraModals={
        <Modal
          open={showVoucherTypePicker}
          setOpen={(isOpen) => !isOpen && setShowVoucherTypePicker(false)}
          maxWidth="md"
          header={<h3 className="text-base font-semibold leading-6 text-gray-900">Add Voucher</h3>}
          footer={
            <div className="flex justify-end gap-x-3">
              <button
                type="button"
                className="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100"
                onClick={() => setShowVoucherTypePicker(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={createSelectedVoucher}
                disabled={!selectedCreateType}
              >
                Continue
              </button>
            </div>
          }
        >
          <Select
            label="Voucher Type"
            options={createVoucherOptions}
            value={selectedCreateType}
            onChange={setSelectedCreateType}
            placeholder="Select voucher type"
            isClearable
            isSearchable={false}
          />
        </Modal>
      }
    />
  );
}
